import Button from '~~elements/Button';
import toast from '~~elements/Toast';
import ProgressBar from '~~elements/ProgressBar';
import { useState } from 'react';
import * as R from 'ramda';
import moment from 'moment';
import { FormalGameResource, GameResource, PlayerResource } from '~~apis/resource';
import { timeDisplayReverse, validEventTimestampDisplay, validTimeDisplay } from '../../../common/utils/video';

export default function ScorebookButton({ tournament, games, className = '' }) {
  const [batchDownload, setBatchDownload] = useState(false);
  const [batchTitle, setBatchTitle] = useState('讀取並轉換資料');
  const [batchCompleted, setBatchCompleted] = useState(0);

  const single = games.length <= 1;
  const [targets, setTargets] = useState(single ? ['scorebook'] : []);

  const [force, setForce] = useState(false);

  const [lastProcessStatus, setLastProcessStatus] = useState('');

  const [partial, setPartial] = useState('');

  const syncto = (scorebookGames, players) => {
    const amount = scorebookGames.length;
    // downloadJSON(`${tournament.title}-逐球數據-${moment().format('YYYYMMDD')}`, scorebookGames, players);
    setBatchTitle(`同步數量： 0 / ${amount}`);
    setBatchCompleted(0);

    const logs = [];
    setLastProcessStatus('同步中');
    new Promise(async (resolve, reject) => {
      let done = 0;
      for (let scorebookGame of scorebookGames) {
        const dosync = async () => {
          const syncedData = await FormalGameResource.syncto({
            seasonUniqid: tournament.uniqid,
            gameUniqid: scorebookGame.uniqid,
            data: {
              ...scorebookGame,
              players: players
                .map((player) => {
                  const exp = player.experiences.find((e) => e.season_uniqid === tournament.uniqid);
                  const playerJSON = {
                    ...player,
                    position: posMapping[player.first_position] ?? player.first_position,
                    tHanded: player.p_hand,
                    bHanded: player.b_hand,
                    number: exp.number,
                    team: exp.team,
                    teamUniqid: exp.team_uniqid,
                  };
                  if (![scorebookGame.game.awayUniqid, scorebookGame.game.homeUniqid].includes(playerJSON.teamUniqid)) {
                    return null;
                  }
                  delete playerJSON.experiences;
                  return playerJSON;
                })
                .filter((p) => !!p),
            },
            force,
            targets,
            partial,
          });
          return syncedData;
        };

        const wait = () => {
          return new Promise((resolve) => setTimeout(resolve, 3000));
        };

        let retry = 3;
        while (retry > 0) {
          try {
            const syncedData = await dosync();
            if (partial && syncedData.updated) {
              logs.push({ seq: scorebookGame.seq, uniqid: scorebookGame.uniqid, ...syncedData });
            }
            done++;
            setBatchTitle(`同步數量： ${done} / ${amount}`);
            setBatchCompleted(((done * 100) / amount).toFixed(1));
            break;
          } catch (e) {
            toast(`發生錯誤：${`G${scorebookGame.seq}：${e.message}`}`, { status: 'error', second: 5 });
            retry--;
            await wait();
          }
        }
      }
      resolve();
    })
      .then(() => {
        setBatchDownload(false);
        toast('處理完成', { status: 'success', second: 5 });
        setLastProcessStatus('同步完成');
        if (logs.length) {
          const jsonl = [];
          logs.forEach((log) => {
            jsonl.push(JSON.stringify(log));
            return;
          });
          const jsonlString = jsonl.join('\n');
          const blob = new Blob([jsonlString], { type: 'text/plain' });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = `${moment().format('YYYYMMDD_HHmmss')}.jsonl`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(a.href);
        }
      })
      .catch((e) => {
        setBatchDownload(false);
        toast(`發生錯誤：${e}`, { status: 'error', second: 5 });
        setLastProcessStatus('同步失敗');
      });
  };

  const handleBatchClick = () => {
    setBatchDownload(true);
    setBatchCompleted(0);

    const finishedGames = R.sort(
      (a, b) => moment(a.info.started_at).unix() - moment(b.info.started_at).unix(),
      R.filter((g) => g.info.status === 'FINISHED', games),
    );
    const amount = finishedGames.length;
    setBatchTitle(`比賽數量： 0 / ${amount}`);

    const scorebookGames = [];
    const players = [];
    const getGameInPlayDetail = () => {
      const finishedGame = finishedGames.splice(0, 1)[0];
      GameResource.getGame({ seasonUniqid: tournament.uniqid, gameUniqid: finishedGame.uniqid })
        .then(({ data }) => {
          console.log(data);

          const scorebookJSON = toScorebook(data);
          scorebookGames.push(scorebookJSON);
          setBatchTitle(`剩餘數量： ${finishedGames.length} / ${amount}`);
          setBatchCompleted((((amount - finishedGames.length) * 100) / amount).toFixed(1));
          if (finishedGames.length > 0) {
            getGameInPlayDetail();
            return;
          }

          const fullRequiredPlayers = R.uniq(R.flatten(scorebookGames.map((g) => g.requiredPlayers)));
          const fullRequiredPlayerAmount = fullRequiredPlayers.length;
          const getPlayers = () => {
            const ps = fullRequiredPlayers.splice(0, 50);
            const next = () => {
              setBatchTitle(`讀取球員資料： ${fullRequiredPlayers.length} / ${fullRequiredPlayerAmount}`);
              setBatchCompleted(
                (((fullRequiredPlayerAmount - fullRequiredPlayers.length) * 100) / fullRequiredPlayerAmount).toFixed(1),
              );
              if (fullRequiredPlayers.length > 0) {
                getPlayers();
                return;
              }
              syncto(scorebookGames, players);
              return;
            };
            if (!ps.length) {
              next();
              return;
            }

            PlayerResource.getPlayers({ playerUniqids: ps }).then(({ data }) => {
              players.push(...data);
              next();
            });
          };
          getPlayers();
        })
        .catch((e) => {
          console.error(e);
          toast(e, { status: 'error' });
        });
    };

    getGameInPlayDetail();
  };

  const MyTarget = (
    <p>
      同步至
      {['scorebook'].map((target) => (
        <label key={target} style={{ marginLeft: '0.8rem' }}>
          <input
            type="checkbox"
            checked={targets.includes(target)}
            onChange={() =>
              setTargets(targets.includes(target) ? targets.filter((t) => t !== target) : [...targets, target])
            }
            disabled={batchDownload}
          />
          {target}
        </label>
      ))}
    </p>
  );

  if (single) {
    return (
      <div>
        <select value={partial} onChange={(e) => setPartial(e.target.value)}>
          <option value="">全部更新</option>
          <option value="BASE">壘包狀態</option>
        </select>
        <Button className={className} disabled={batchDownload} onClick={handleBatchClick}>
          開始同步
        </Button>
        <p>{lastProcessStatus}</p>
        <label style={{ marginLeft: '1rem' }}>
          重洗（裡面原有資料會刪除後新增）
          <input type="checkbox" checked={force} onChange={() => setForce(!force)} />
        </label>
        {MyTarget}
      </div>
    );
  }

  return (
    <>
      <h4 className="admin-only--title">賽季完成賽事同步至 Scorebook</h4>
      <div className="admin-only--content">
        <i style={{ color: 'red' }}>*如非急迫需求建議從比賽介面逐場更新</i>
      </div>
      <div className="admin-only--content">
        <i>依照數量決定要等多久。</i>
      </div>
      <div className="admin-only--content">
        <select value={partial} onChange={(e) => setPartial(e.target.value)}>
          <option value="">全部更新</option>
          <option value="BASE">壘包狀態</option>
        </select>
        <Button width="10rem" color="red" disabled={batchDownload || targets.length === 0} onClick={handleBatchClick}>
          開始同步
        </Button>
        <label style={{ marginLeft: '1rem' }}>
          強制刪除後新增
          <input type="checkbox" checked={force} onChange={() => setForce(!force)} disabled={batchDownload} />
        </label>
        {MyTarget}
      </div>
      <div
        className="admin-only--content"
        style={{ background: 'linear-gradient(242deg, #094bcd, #28cfd7)', padding: '0.5rem' }}
      >
        <ProgressBar isOpen={true} title={batchTitle} completed={batchCompleted} />
      </div>
    </>
  );
}

const posMapping = {
  1: 'P',
  2: 'C',
  3: 'FB',
  4: 'SB',
  5: 'TB',
  6: 'SS',
  7: 'LF',
  8: 'CF',
  9: 'RF',
  DH: 'DH',
  '1B': 'FB',
  '2B': 'SB',
  '3B': 'TB',
};

const downloadJSON = (title, scorebookGames, players) => {
  const jsonl = [];
  players.forEach((player) => {
    const playerJSON = {
      name: player.name,
      position: posMapping[player.first_position] ?? player.first_position,
      tHanded: player.p_hand,
      bHanded: player.b_hand,
      number: player.experiences[0].number,
      team: player.experiences[0].team,
    };
    jsonl.push(JSON.stringify(playerJSON));
    return;
  });
  scorebookGames.forEach((game) => {
    jsonl.push(JSON.stringify(game.game));
    jsonl.push(...game.events.map((e) => JSON.stringify(e)));
    return;
  });
  const jsonlString = jsonl.join('\n');

  const blob = new Blob([jsonlString], { type: 'text/plain' });

  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${title}.jsonl`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
};

const SCOREBOOK_TIMING_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS';
const toRunnerType = (code) => {
  switch (code) {
    case 'SB':
      return 'SB';
    case 'CS':
      return 'CS';
    case 'CS_E':
      return 'CS_E';
    case 'BALK':
      return 'BALK';
    default:
      return 'ADVANCED';
  }
};

const normalizeBase = (base) => {
  return Number.isInteger(base) ? base : 0;
};

const getEndBaseRunnerUniqids = (event, eventRunners) => {
  const baseState = {
    1: event.first?.uniqid ?? '',
    2: event.second?.uniqid ?? '',
    3: event.third?.uniqid ?? '',
  };

  const latestRunnerByUniqid = new Map();
  eventRunners.forEach(({ source }) => {
    const runnerUniqid = source?.runner?.uniqid;
    if (!runnerUniqid) {
      return;
    }
    latestRunnerByUniqid.set(runnerUniqid, source);
  });

  latestRunnerByUniqid.forEach((runner, runnerUniqid) => {
    [1, 2, 3].forEach((base) => {
      if (baseState[base] === runnerUniqid) {
        baseState[base] = '';
      }
    });

    if (runner.is_out || runner.is_scoring) {
      return;
    }

    const endBase = normalizeBase(runner.end_base);
    if ([1, 2, 3].includes(endBase)) {
      baseState[endBase] = runnerUniqid;
    }
  });

  return {
    endFirstUniqid: baseState[1],
    endSecondUniqid: baseState[2],
    endThirdUniqid: baseState[3],
  };
};

const toScorebook = (game) => {
  const requiredPlayers = [];
  const startedAt = moment(game.info.started_at);
  const gJSON = {
    // _id
    group: game.season.title,
    seq: `00${game.seq}`.slice(-3),
    // awayId
    awayUniqid: game.away.team_uniqid,
    away: game.away.team,
    // homeId
    homeUniqid: game.home.team_uniqid,
    home: game.home.team,
    awayRuns: game.away.runs,
    homeRuns: game.home.runs,
    awayHits: game.away.hits,
    homeHits: game.home.hits,
    awayErrors: game.away.errors,
    homeErrors: game.home.errors,
    startedAt: game.info.started_at,
    tags: ['REBAS'],
    view: 'P',
    createdAt: game.info.started_at,
    endedAt: game.info.ended_at,
    awayInningRuns: game.away.scores.map((s) => parseInt(s)),
    homeInningRuns: game.home.scores.map((s) => parseInt(s)),
    stadium: game.info.location,
    umpireInChief: game.info.umpire?.umpireInChief,
    // 影片不由 Rebas 同步
    // videos: [
    //   {
    //     link:
    //       `https://scorebook-rtmp.rebas.com.tw/rebas_videos` +
    //       `/${startedAt.format('YYYY')}` +
    //       `/G${game.seq}_${startedAt.format('YYYYMMDD')}.mp4`,
    //     gcsLink:
    //       `https://storage.googleapis.com/strikezone-hunter-rebas-tw-cpbl` +
    //       `/${startedAt.format('YYYY')}` +
    //       `/G${game.seq}_${startedAt.format('YYYYMMDD')}.mp4`,
    //     startedAt: startedAt.format(SCOREBOOK_TIMING_FORMAT),
    //     type: 'VIDEO',
    //   },
    // ],
    awayLineup: toScorebookLineup(game.away.box),
    homeLineup: toScorebookLineup(game.home.box),
  };

  let outs = 0;
  let preSide = game.PA_list[0].side;
  const eventsJSON = R.flatten(
    game.PA_list.map((PA) => {
      const fielders = PA.fielders
        .map((f) => {
          requiredPlayers.push(f?.fielder?.uniqid);
          return {
            // id
            playerUniqid: f?.fielder?.uniqid,
            player: f?.fielder?.name,
            pos: posMapping[f?.position],
          };
        })
        .filter((f) => !!f.pos);

      let awayScores = PA.start_away_score;
      let homeScores = PA.start_home_score;
      let balls = 0;
      let strikes = 0;
      if (preSide !== PA.side) {
        outs = 0;
        preSide = PA.side;
      }

      return PA.events.map((e, eIndex) => {
        if (!['NO_PITCH', 'PITCH', 'BASE'].includes(e.type)) {
          return null;
        }

        const currentSBO = {
          awayScores,
          homeScores,
          balls,
          strikes,
          outs,
        };

        if (e.is_strike && strikes < 2) {
          strikes++;
        } else if (e.is_ball && balls < 3) {
          balls++;
        }
        const eventRunners = PA.runners
          .filter((r) => r.event_index === eIndex)
          .map((r) => ({
            source: r,
            runnerIndex: -1,
          }))
          .map((r, runnerIndex) => ({
            ...r,
            runnerIndex,
          }))
          .map(({ source, runnerIndex }) => ({
            source,
            runnerIndex,
            eventRunner: {
              runnerUniqid: source.runner.uniqid,
              type: toRunnerType(source.code),
              startBase: normalizeBase(source.start_base),
              endBase: normalizeBase(source.end_base),
            },
          }));
        eventRunners.forEach(({ source: r }) => {
          if (r.is_scoring) {
            if (PA.side === 'AWAY') {
              awayScores++;
            } else {
              homeScores++;
            }
          }
          if (r.is_out) {
            outs++;
          }
        });

        const sbList = eventRunners
          .filter(({ source: r }) => ['SB', 'CS', 'CS_E'].includes(r.code))
          .map(({ source: r, runnerIndex }) => {
            requiredPlayers.push(r.runner.uniqid);
            return {
              // id
              runnerUniqid: r.runner.uniqid,
              runner: r.runner.name,
              desc: r.end_base === 0 ? 'thirdId' : r.end_base === 2 ? 'firstId' : 'secondId',
              type: r.code === 'SB' ? 'SB' : 'CS',
              runnerIndex,
            };
          });
        const runList = eventRunners
          .filter(({ source: r }) => r.is_scoring)
          .map(({ source: r, runnerIndex }) => {
            requiredPlayers.push(r.runner.uniqid);
            return {
              // id
              runnerUniqid: r.runner.uniqid,
              runner: r.runner.name,
              // 應該要是投手 id
              descUniqid: r.count_on_pitcher.uniqid,
              desc: r.count_on_pitcher.name,
              type: r.is_ER ? 'ER' : 'R',
              asBatter: r.is_RBI ? 'RBI' : '_',
              runnerIndex,
            };
          });
        const outList = eventRunners
          .filter(({ source: r }) => r.is_out)
          .map(({ source: r, runnerIndex }) => {
            requiredPlayers.push(r.runner.uniqid);
            return {
              // id
              runnerUniqid: r.runner.uniqid,
              runner: r.runner.name,
              desc: '',
              type: r.code === 'CS' ? 'CS' : e.code === 'PO' ? 'PICKOFF' : 'N',
              runnerIndex,
            };
          });
        const errorList = [];
        const { endFirstUniqid, endSecondUniqid, endThirdUniqid } = getEndBaseRunnerUniqids(e, eventRunners);

        requiredPlayers.push(e.pitcher.uniqid, e.catcher.uniqid, e.batter.uniqid);
        ['first', 'second', 'third'].forEach((column) => {
          if (e[column]) {
            requiredPlayers.push(e[column].uniqid);
          }
          return;
        });

        const isLastEventOfPA = eIndex === PA.events.length - 1;

        return {
          // _id
          // gameId
          stadium: game.info.location,
          umpireInChief: game.info.umpire?.umpireInChief,
          date: startedAt.format('YYYY-MM-DD'),
          eType: e.type === 'PITCH' ? 'P' : e.type === 'NO_PITCH' ? 'NO_PITCH' : sbList.length > 0 ? 'SB' : 'PICKOFF',
          ...currentSBO,
          inning: PA.inning,
          side: PA.side,
          // pTeamId
          pTeamUniqid: game[PA.side === 'AWAY' ? 'home' : 'away'].team_uniqid,
          pTeam: game[PA.side === 'AWAY' ? 'home' : 'away'].team,
          // pitcherId
          pitcherUniqid: e.pitcher.uniqid,
          pitcher: e.pitcher.name,
          pHand: PA.pitcher.p_hand,
          // catcherId
          catcherUniqid: e.catcher.uniqid,
          catcher: e.catcher.name,
          // bTeamId
          bTeamUniqid: game[PA.side === 'AWAY' ? 'away' : 'home'].team_uniqid,
          bTeam: game[PA.side === 'AWAY' ? 'away' : 'home'].team,
          // batterId
          batterUniqid: e.batter.uniqid,
          batter: e.batter.name,
          bHand: PA.batter.b_hand,
          paRound: PA.PA_round,
          paOrder: PA.PA_order,
          // firstId
          firstUniqid: e.first ? e.first.uniqid : '',
          first: e.first ? e.first.name : '',
          // secondId
          secondUniqid: e.second ? e.second.uniqid : '',
          second: e.second ? e.second.name : '',
          // thirdId
          thirdUniqid: e.third ? e.third.uniqid : '',
          third: e.third ? e.third.name : '',
          signinAt: validTimeDisplay(e.video_signin_at)
            ? moment(startedAt).add(timeDisplayReverse(e.video_signin_at), 'seconds').format(SCOREBOOK_TIMING_FORMAT)
            : validEventTimestampDisplay(e.video_signin_at)
            ? e.video_signin_at
            : '',
          startedAt: validTimeDisplay(e.video_started_at)
            ? moment(startedAt).add(timeDisplayReverse(e.video_started_at), 'seconds').format(SCOREBOOK_TIMING_FORMAT)
            : validEventTimestampDisplay(e.video_started_at)
            ? e.video_started_at
            : '',
          contactAt: validTimeDisplay(e.video_contact_at)
            ? moment(startedAt).add(timeDisplayReverse(e.video_contact_at), 'seconds').format(SCOREBOOK_TIMING_FORMAT)
            : validEventTimestampDisplay(e.video_contact_at)
            ? e.video_contact_at
            : '',
          flyCatchAt: validTimeDisplay(e.video_fly_catch_at)
            ? moment(startedAt).add(timeDisplayReverse(e.video_fly_catch_at), 'seconds').format(SCOREBOOK_TIMING_FORMAT)
            : validEventTimestampDisplay(e.video_fly_catch_at)
            ? e.video_fly_catch_at
            : '',
          groundCatchAt: validTimeDisplay(e.video_ground_catch_at)
            ? moment(startedAt)
                .add(timeDisplayReverse(e.video_ground_catch_at), 'seconds')
                .format(SCOREBOOK_TIMING_FORMAT)
            : validEventTimestampDisplay(e.video_ground_catch_at)
            ? e.video_ground_catch_at
            : '',
          sbThrowAt: validTimeDisplay(e.video_sb_throw_at)
            ? moment(startedAt).add(timeDisplayReverse(e.video_sb_throw_at), 'seconds').format(SCOREBOOK_TIMING_FORMAT)
            : validEventTimestampDisplay(e.video_sb_throw_at)
            ? e.video_sb_throw_at
            : '',
          sbCatchAt: validTimeDisplay(e.video_sb_catch_at)
            ? moment(startedAt).add(timeDisplayReverse(e.video_sb_catch_at), 'seconds').format(SCOREBOOK_TIMING_FORMAT)
            : validEventTimestampDisplay(e.video_sb_catch_at)
            ? e.video_sb_catch_at
            : '',
          pickoffAt: '',
          velo: e.pitch?.velocity ?? '',
          rpm: e.pitch?.RPM ?? '',
          coordX: e.pitch?.coord_x ?? '',
          coordY: e.pitch?.coord_y ?? '',
          pType: e.pitch?.type === 'FA' ? 'FF' : e.pitch?.type === 'FT' ? 'SI' : e.pitch?.type ?? '',
          ev: e.pitch?.EV ?? '',
          traj: e.pitch?.trajectory || isLastEventOfPA ? PA.trajectory ?? '' : '',
          hardness: e.pitch?.hardness ?? '',
          locationCode: e.pitch?.location_code && e.pitch?.location_coord_x ? PA.location_code ?? '' : '',
          locationCoordX: e.pitch?.location_coord_x ?? '',
          locationCoordY: e.pitch?.location_coord_y ?? '',
          ballPassed: e.pitch?.fielder_position ?? '',
          isStrike: e.is_strike,
          call: e.pitch?.code ?? '',
          result: isLastEventOfPA ? PA.result.split('_')[0] : '',
          sbsOnPlay: sbList.length,
          runsOnPlay: runList.length,
          outsOnPlay: outList.length,
          errorsOnPlay: errorList.length,
          sbList,
          runList,
          outList,
          errorList,
          runners: eventRunners.map((r) => r.eventRunner),
          endFirstUniqid,
          endSecondUniqid,
          endThirdUniqid,

          fielders,
          videoFragments: [],
        };
      });
    }),
  ).filter((e) => !!e);

  const recordsJSON = [
    ...game.away.box.records.map((r) => ({
      ...r,
      side: 'AWAY',
    })),
    ...game.home.box.records.map((r) => ({
      ...r,
      side: 'HOME',
    })),
  ].map((r) => {
    return {
      // gameId
      date: startedAt.format('YYYY-MM-DD'),
      side: r.side,
      // teamId
      teamUniqid: game[r.side === 'AWAY' ? 'away' : 'home'].team_uniqid,
      team: game[r.side === 'AWAY' ? 'away' : 'home'].team,
      // playerId
      playerUniqid: r.player.uniqid,
      player: r.player.name,
      record: r.value,
    };
  });

  console.log(gJSON);
  console.log(eventsJSON);
  console.log(recordsJSON);

  return { uniqid: game.uniqid, seq: game.seq, game: gJSON, events: eventsJSON, records: recordsJSON, requiredPlayers };
};

const toScorebookLineup = (box) => {
  return {
    batters: Array.from({ length: 9 }, (_, i) => i + 1).map((order) => {
      const orderBatters = R.filter((b) => b.order === order, box.battings);
      let position = '';
      R.reverse(orderBatters).forEach((b) => {
        if (position) {
          return;
        }
        const fielder = R.findLast((f) => f.player.uniqid === b?.player.uniqid, box.fieldings);
        if (!fielder || fielder.position === 'PH' || fielder.position === 'PR') {
          return;
        }
        position = fielder?.position;
        if (position === '1B') {
          position = 'FB';
        } else if (position === '2B') {
          position = 'SB';
        } else if (position === '3B') {
          position = 'TB';
        }
      });
      const lastBatter = R.findLast((b) => b.order === order, box.battings);
      return {
        // batterId
        batterUniqid: lastBatter?.player.uniqid,
        batter: lastBatter?.player.name,
        position,
      };
    }),
    // pitcherId
    pitcherUniqid: R.reverse(box.pitchings)[0].player.uniqid,
    pitcher: R.reverse(box.pitchings)[0].player.name,
  };
};
